import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { useLang } from "../i18n";

export default function Header() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const links = [
    { to: "/oeuvres", label: t("nav.works"), id: "nav-works-link" },
    { to: "/presentation", label: t("nav.presentation"), id: "nav-presentation-link" },
    { to: "/contact", label: t("nav.contact"), id: "nav-contact-link" },
  ];
  const onDark = location.pathname.startsWith("/oeuvres") && !location.pathname.includes("/oeuvres/") ? false : false;

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 border-b"
      style={{
        background: "rgba(250,249,246,0.85)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        borderColor: "rgba(229,226,220,0.7)",
      }}
    >
      <div className="px-6 sm:px-12 lg:px-20 h-16 flex items-center justify-between">
        <Link to="/" aria-label="Accueil" data-testid="nav-home-link">
          <Logo />
        </Link>
        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={({ isActive }) =>
                `link-line text-sm tracking-wide ${isActive ? "text-[#C83E2B]" : "text-[#121212]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-6">
          <div className="font-mono-meta text-xs tracking-[0.2em] flex items-center gap-2" data-testid="lang-toggle">
            <button
              data-testid="lang-fr-btn"
              onClick={() => setLang("fr")}
              className={lang === "fr" ? "text-[#121212]" : "text-[#8C8983] hover:text-[#121212]"}
            >
              FR
            </button>
            <span className="text-[#C5C0B7]">|</span>
            <button
              data-testid="lang-en-btn"
              onClick={() => setLang("en")}
              className={lang === "en" ? "text-[#121212]" : "text-[#8C8983] hover:text-[#121212]"}
            >
              EN
            </button>
          </div>
          <Link
            to="/oeuvres"
            data-testid="nav-book-cta"
            className="hidden lg:inline-flex items-center gap-2 font-mono-meta text-[11px] uppercase tracking-[0.18em] border border-[#121212] px-4 py-2 hover:bg-[#121212] hover:text-[#FAF9F6] transition-colors duration-300"
          >
            {t("nav.book")}
          </Link>
          <button
            data-testid="mobile-menu-btn"
            className="md:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden border-t"
            style={{ borderColor: "#E5E2DC", background: "#FAF9F6" }}
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="font-serif-display text-3xl"
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
